import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  Building2, 
  Sparkles, 
  MessageCircle, 
  Newspaper, 
  Megaphone, 
  CheckCircle2, 
  Phone, 
  MapPin, 
  ArrowRight,
  Menu,
  X,
  Zap,
  Shield,
  Users,
  Globe
} from 'lucide-react';
import './App.css';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <motion.div 
            className="nav-logo"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="logo-icon">
              <Building2 size={28} />
            </div>
            <span className="logo-text">DigitalCity.ID</span>
          </motion.div>

          <div className="nav-links desktop-nav">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Home</a>
            <a href="#tentang" onClick={(e) => { e.preventDefault(); scrollToSection('tentang'); }}>Tentang</a>
            <a href="#layanan" onClick={(e) => { e.preventDefault(); scrollToSection('layanan'); }}>Layanan</a>
            <a href="#kontak" onClick={(e) => { e.preventDefault(); scrollToSection('kontak'); }}>Kontak</a>
          </div>

          <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <motion.div 
          className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}
          initial={false}
          animate={{ x: isMenuOpen ? 0 : '100%' }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <div className="mobile-menu-links">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Home</a>
            <a href="#tentang" onClick={(e) => { e.preventDefault(); scrollToSection('tentang'); }}>Tentang Kami</a>
            <a href="#layanan" onClick={(e) => { e.preventDefault(); scrollToSection('layanan'); }}>Layanan</a>
            <a href="#kontak" onClick={(e) => { e.preventDefault(); scrollToSection('kontak'); }}>Kontak</a>
          </div>
        </motion.div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-bg-pattern"></div>
        <motion.div 
          className="hero-container"
          style={{ opacity, scale }}
        >
          <div className="hero-content">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="hero-badge">
                <Sparkles size={16} />
                <span>AI-Powered City Information</span>
              </div>
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="gradient-text">DigitalCity.ID</span>
            </motion.h1>

            <motion.p
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Layanan informasi kota berbasis Artificial Intelligence yang menghubungkan{' '}
              <strong>pemerintah kota dan warga</strong> melalui kanal yang paling dekat dengan keseharian masyarakat:{' '}
              <strong>WhatsApp</strong>.
            </motion.p>

            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              Didukung oleh berita resmi dan kredibel dari Kantor Berita Antara, DigitalCity.ID menyajikan informasi publik yang{' '}
              <strong>ringkas, relevan, dan mudah dipahami</strong>.
            </motion.p>

            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <button className="btn btn-primary" onClick={() => scrollToSection('layanan')}>
                Pelajari Layanan
                <ArrowRight size={20} />
              </button>
              <button className="btn btn-secondary" onClick={() => scrollToSection('kontak')}>
                Hubungi Kami
              </button>
            </motion.div>
          </div>

          <div className="hero-visual">
            <motion.div
              className="floating-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="card-glow"></div>
              <div className="feature-item">
                <div className="feature-icon whatsapp">
                  <MessageCircle size={32} />
                </div>
                <div className="feature-text">
                  <h4>WhatsApp Integration</h4>
                  <p>Komunikasi langsung dengan warga</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="floating-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <div className="card-glow"></div>
              <div className="feature-item">
                <div className="feature-icon news">
                  <Newspaper size={32} />
                </div>
                <div className="feature-text">
                  <h4>Berita Antara</h4>
                  <p>Sumber terpercaya & kredibel</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="floating-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              <div className="card-glow"></div>
              <div className="feature-item">
                <div className="feature-icon ai">
                  <Sparkles size={32} />
                </div>
                <div className="feature-text">
                  <h4>AI-Powered</h4>
                  <p>Kurasi & ringkasan cerdas</p>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Value Proposition */}
      <section className="value-prop">
        <motion.div
          className="value-card"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Zap className="value-icon" size={48} />
          <h2>Komunikasi Publik yang Modern, Inklusif, dan Terpercaya</h2>
          <p>Tanpa membebani APBD</p>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="tentang" className="about">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Tentang Kami</h2>
            <div className="title-underline"></div>
          </motion.div>

          <div className="about-grid">
            <motion.div
              className="about-card"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="about-icon">
                <Building2 size={32} />
              </div>
              <h3>PT AI Indonesia</h3>
              <p>
                Perusahaan teknologi yang berfokus pada pengembangan solusi Artificial Intelligence untuk sektor publik, korporasi, dan institusi strategis.
              </p>
            </motion.div>

            <motion.div
              className="about-card"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="about-icon">
                <Shield size={32} />
              </div>
              <h3>Technology Enabler</h3>
              <p>
                Menghadirkan AI Agents yang mengkurasi, merangkum, dan mendistribusikan informasi secara cerdas tanpa mengubah isi maupun independensi editorial sumber berita.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="mission-card"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Globe size={64} className="mission-icon" />
            <h3>Inovasi untuk Indonesia</h3>
            <p>Solusi AI yang memberdayakan sektor publik dan masyarakat</p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="layanan" className="services">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Layanan Kami</h2>
            <div className="title-underline"></div>
            <p className="section-subtitle">DigitalCity.ID – AI Information Agent untuk Kota</p>
          </motion.div>

          <div className="services-grid">
            {[
              {
                icon: <Megaphone size={32} />,
                title: 'Pengumuman Resmi',
                description: 'Penyampaian pengumuman resmi pemerintah kota',
                color: 'blue',
                delay: 0.2
              },
              {
                icon: <Newspaper size={32} />,
                title: 'Ringkasan Berita',
                description: 'Ringkasan berita daerah dari Antara yang relevan bagi warga',
                color: 'green',
                delay: 0.3
              },
              {
                icon: <MessageCircle size={32} />,
                title: 'Tanya-Jawab',
                description: 'Layanan tanya–jawab informasi kota melalui WhatsApp',
                color: 'purple',
                delay: 0.4
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Terverifikasi',
                description: 'Kanal komunikasi publik yang cepat, konsisten, dan terverifikasi',
                color: 'orange',
                delay: 0.5
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                className={`service-card ${service.color}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: service.delay }}
                whileHover={{ y: -10, transition: { duration: 0.3 } }}
              >
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="business-model"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="business-icon">
              <Users size={32} />
            </div>
            <div className="business-content">
              <h3>Model Bisnis yang Berkelanjutan</h3>
              <p>
                Layanan ini bersifat <strong>public-facing</strong>, menggunakan <strong>bahasa Indonesia</strong>, dan didukung model <strong>iklan serta sponsorship</strong>, sehingga pemerintah kota menjadi <strong>penerima manfaat, bukan pembayar</strong>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontak" className="contact">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Hubungi Kami</h2>
            <div className="title-underline"></div>
          </motion.div>

          <div className="contact-grid">
            <motion.div
              className="contact-info"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h3>PT AI Indonesia</h3>
              <div className="contact-items">
                <div className="contact-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <h4>Alamat</h4>
                    <p>Office 88, Lantai 32<br />Kawasan Kota Kasablanka<br />Jakarta 12870 – Indonesia</p>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h4>Telepon</h4>
                    <a href="tel:087883468831">0878 8346 8831</a>
                  </div>
                </div>
                <div className="contact-item">
                  <div className="contact-icon">
                    <MessageCircle size={24} />
                  </div>
                  <div>
                    <h4>WhatsApp</h4>
                    <a href="https://wa.me/6287883468831" target="_blank" rel="noopener noreferrer">
                      Chat via WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="contact-cta"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3>Siap Membangun Kota Digital?</h3>
              <p>
                Hubungi kami untuk mengetahui bagaimana DigitalCity.ID dapat membantu kota Anda membangun komunikasi publik yang modern dan terpercaya.
              </p>
              <div className="cta-benefits">
                <div className="benefit">
                  <CheckCircle2 size={20} />
                  <span>Konsultasi gratis</span>
                </div>
                <div className="benefit">
                  <CheckCircle2 size={20} />
                  <span>Implementasi cepat</span>
                </div>
                <div className="benefit">
                  <CheckCircle2 size={20} />
                  <span>Tanpa biaya APBD</span>
                </div>
              </div>
              <a 
                href="https://wa.me/6287883468831" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                <MessageCircle size={20} />
                Hubungi Sekarang
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-col">
              <div className="footer-logo">
                <Building2 size={24} />
                <span>DigitalCity.ID</span>
              </div>
              <p>AI Information Agent untuk Kota - Menghubungkan pemerintah dan warga melalui WhatsApp</p>
            </div>
            <div className="footer-col">
              <h4>Navigasi</h4>
              <ul>
                <li><a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }}>Home</a></li>
                <li><a href="#tentang" onClick={(e) => { e.preventDefault(); scrollToSection('tentang'); }}>Tentang Kami</a></li>
                <li><a href="#layanan" onClick={(e) => { e.preventDefault(); scrollToSection('layanan'); }}>Layanan</a></li>
                <li><a href="#kontak" onClick={(e) => { e.preventDefault(); scrollToSection('kontak'); }}>Kontak</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Kontak</h4>
              <ul>
                <li><Phone size={16} /> 0878 8346 8831</li>
                <li><MapPin size={16} /> Jakarta, Indonesia</li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2024 PT AI Indonesia. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
